import { Bot, BookOpenCheck, CalendarClock, CheckCircle2, ExternalLink, ShieldCheck } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHero } from '../components/PageHero'
import { PageMeta } from '../components/PageMeta'
import { Reveal } from '../components/Reveal'

export default function EditorialPolicyPage() {
  return (
    <>
      <PageMeta title="Editorial policy" description="How the Fathima Finu health library selects sources, uses AI-assisted drafting and illustration, checks medical boundaries, labels dates, and handles corrections." />
      <PageHero
        eyebrow="Editorial policy"
        title={<>Useful health writing needs <span className="serif-accent">visible standards.</span></>}
        copy="This page explains where the guides come from, what AI helps with, what a source-check date means, and what the articles can never replace."
        aside={<div className="privacy-seal"><BookOpenCheck size={29} /><strong>Traceable by design</strong><span>Every health guide links to the authoritative source used for its central claims.</span></div>}
      />

      <section className="section editorial-principles-section">
        <div className="container editorial-principles-grid">
          {[
            { icon: BookOpenCheck, title: 'Start with accountable sources', copy: 'Core claims are based on public guidance from organizations such as WHO, national public-health agencies, regulators, and specialist government health institutes.' },
            { icon: Bot, title: 'Disclose AI assistance', copy: 'AI may help organize a first draft, create concept art, or improve readability. It does not examine a patient, establish a diagnosis, or replace accountable human editorial judgment.' },
            { icon: ShieldCheck, title: 'Keep the safety boundary visible', copy: 'Each guide distinguishes general education from personal care and uses local-emergency-service language when a warning pattern may be urgent.' },
          ].map((item, index) => <Reveal className="editorial-principle" key={item.title} delay={index * 0.06}><item.icon size={25} /><h2>{item.title}</h2><p>{item.copy}</p></Reveal>)}
        </div>
      </section>

      <section className="section editorial-detail-section">
        <div className="container editorial-detail-grid">
          <Reveal>
            <span className="eyebrow"><CalendarClock size={15} /> Dates that mean what they say</span>
            <h2>Published, source-checked, and medically reviewed are <span className="serif-accent">different claims.</span></h2>
            <p>Article dates identify when a guide was published and its linked source was checked. A guide will only display a named medical-review credential after that person has actually reviewed and approved the article. Older content should be rechecked when official guidance changes.</p>
            <p>Research Watch uses status labels such as approved, cleared, and investigational. These labels describe a specific source and jurisdiction; they do not promise access, suitability, or benefit for an individual.</p>
          </Reveal>
          <Reveal className="editorial-checklist" delay={0.08}>
            <strong>Before a guide is considered ready</strong>
            {[
              'The central source opens and supports the main claims.',
              'Urgent warning signs are separated from routine self-care.',
              'Medicine changes are not recommended without a clinician.',
              'The language avoids certainty, fear, and guaranteed outcomes.',
              'A publication date and source-check date remain visible.',
            ].map((item) => <span key={item}><CheckCircle2 size={18} />{item}</span>)}
          </Reveal>
        </div>
      </section>

      <section className="section corrections-section">
        <div className="container corrections-panel">
          <div><span className="eyebrow">Corrections & limitations</span><h2>Health information changes. Good publishing makes room to correct it.</h2><p>If an official source changes, the related guide should be reviewed and updated or withdrawn. Readers can use the private community form to flag a source problem without sharing medical records or personal health details.</p></div>
          <div className="corrections-actions"><Link className="button" to="/community">Flag a content issue</Link><a className="text-link" href="https://www.who.int/about/policies/publishing/copyright" target="_blank" rel="noreferrer">WHO publishing information <ExternalLink size={15} /></a></div>
        </div>
      </section>
    </>
  )
}
